import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import HolidayManagement from './HolidayManagement';
import TimeTable from '../SIS/TimeTable';
import TimeTableUpload from '../SIS/TimeTableUpload';
import TeacherSchedule from './TeacherSchedule';
import ModifyTimeTable from './ModifyTimeTable';

interface ScheduleModuleProps {
  domain: string;
  onBack: () => void;
}

const ScheduleModule: React.FC<ScheduleModuleProps> = ({ domain, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('holidays');

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/holidays')) setActiveTab('holidays');
    else if (path.includes('/timetable-upload')) setActiveTab('timetable-upload');
    else if (path.includes('/timetable-modify')) setActiveTab('timetable-modify');
    else if (path.includes('/teacher-schedule')) setActiveTab('teacher-schedule');
    else if (path.includes('/timetable')) setActiveTab('timetable');
    else setActiveTab('holidays');
  }, [location.pathname]);

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white p-3 shadow-sm">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0">
              <i className="bi bi-calendar3 me-2"></i>
              Schedule Management
            </h4>
            <small>Manage holidays, events, and academic calendar</small>
          </div>
          <button className="btn btn-light btn-sm" onClick={onBack}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm sticky-top" style={{ top: 0, zIndex: 100 }}>
        <div className="container-fluid">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'holidays' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/schedule/holidays')}
              >
                <i className="bi bi-calendar-event me-2"></i>
                Holidays
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'timetable' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/schedule/timetable')}
              >
                <i className="bi bi-calendar3 me-2"></i>
                View Time Table
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'timetable-upload' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/schedule/timetable-upload')}
              >
                <i className="bi bi-upload me-2"></i>
                Upload Time Table
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'timetable-modify' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/schedule/timetable-modify')}
              >
                <i className="bi bi-pencil-square me-2"></i>
                Modify Time Table
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'teacher-schedule' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/schedule/teacher-schedule')}
              >
                <i className="bi bi-person-badge me-2"></i>
                Teacher Schedule
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Content Area */}
      <div className="container-fluid py-4">
        <Routes>
          <Route index element={<HolidayManagement domain={domain} />} />
          <Route path="holidays" element={<HolidayManagement domain={domain} />} />
          <Route path="timetable" element={<TimeTable domain={domain} />} />
          <Route path="timetable-upload" element={<TimeTableUpload domain={domain} />} />
          <Route path="timetable-modify" element={<ModifyTimeTable domain={domain} />} />
          <Route path="teacher-schedule" element={<TeacherSchedule domain={domain} />} />
        </Routes>
      </div>

      {/* Information Footer */}
      <div className="container-fluid py-3">
        <div className="alert alert-info">
          <h6 className="alert-heading">
            <i className="bi bi-info-circle me-2"></i>
            Schedule Module Features
          </h6>
          <hr />
          <div className="row">
            <div className="col-md-6">
              <strong>Holiday Management:</strong>
              <ul className="small mb-0 mt-2">
                <li>Add and manage school holidays, public holidays, and optional holidays</li>
                <li>View holidays organized by month in a clean card layout</li>
                <li>Export holiday list to Excel for printing and distribution</li>
                <li>Filter holidays by year</li>
                <li>Track different types of holidays (Public, School, Optional)</li>
              </ul>
            </div>
            <div className="col-md-6">
              <strong>Time Table Management:</strong>
              <ul className="small mb-0 mt-2">
                <li>View time tables for all classes and sections</li>
                <li>Upload time tables in bulk using Excel templates</li>
                <li>Manage daily schedules with subject assignments</li>
                <li>Track teacher assignments and room allocations</li>
                <li>Export time tables for distribution</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModule;
